import React from 'react';
import PropTypes from 'prop-types';
import {
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { formatChallengeValue } from '../../../utils/challengeFormat';

/**
 * Who a head's figure came from.
 *
 * A head (PM / CRM / SOGO / OGM) records no payments themselves — their people
 * do — so their figure is the net of the team beneath them. This lists that
 * team with what each put in and their share, so a head's number on the board
 * is never a lump sum nobody can account for.
 *
 * The rows are the `members` the analytics response already carries for the
 * head; nothing is fetched here.
 */
export default function TeamMembersDialog({ head, type, open, setOpen }) {
  if (!head) return null;
  const members = [...(head.members || [])].sort((a, b) => Number(b.achieved) - Number(a.achieved));
  const total = Number(head.achieved) || 0;

  return (
    <GlobalDialog
      maxWidth="sm"
      fullWidth
      title={`${head.user_name} — team`}
      open={open}
      setOpen={setOpen}
      closeButton
      closeCondition={() => setOpen(false)}
      content={
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {formatChallengeValue(total, type)} from {members.length}{' '}
            {members.length === 1 ? 'person' : 'people'}
          </Typography>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Contributed</TableCell>
                  <TableCell sx={{ width: 150 }}>Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m, index) => {
                  const share = total > 0 ? Math.round((Number(m.achieved) / total) * 100) : 0;
                  return (
                    <TableRow key={m.user_id} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {m.user_name}
                        {m.is_head && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.disabled' }}>
                            (self)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{m.role_label}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {formatChallengeValue(m.achieved, type)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, share)}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 999 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 30 }}>
                            {share}%
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      }
    />
  );
}

TeamMembersDialog.propTypes = {
  /** A head's participant row from the analytics response, with `members`. */
  head: PropTypes.object,
  type: PropTypes.string,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
